import { lazy } from "react";
import { adminElement, protectedElement, printableElement } from "./adminRouteHelpers";

// All routed pages are lazy-loaded so each feature ships as its own
// Vite chunk. The single index-*.js chunk used to be ~1.94 MB; with
// per-route splitting the first-paint cost drops to the shell + the
// current route's chunk only. <Suspense> + a small RouteFallback
// spinner are wired into the {admin,protected,printable}Element
// helpers, so individual entries don't need their own boilerplate.
//
// Helper that converts a named export into a default export so it can
// be consumed by React.lazy (which requires { default: Component }).
const named = <T extends string>(name: T) =>
  <M extends Record<string, any>>(mod: M) => ({ default: mod[name] });

// ── Pages / dashboards ───────────────────────────────────────
const Home = lazy(() => import("../../pages").then(named("Home")));
const Profile = lazy(() => import("../../pages").then(named("Profile")));
const Settings = lazy(() => import("../../pages").then(named("Settings")));
const AdminDashboard = lazy(() => import("../../features/admin/AdminDashboard"));
const RegisterUserForm = lazy(() => import("../../features/admin/RegisterUserForm"));
const UserList = lazy(() => import("../../features/admin/UserList"));
const RolesList = lazy(() => import("../../features/roles/pages/RolesList"));

// ── Brand / Category / Product ───────────────────────────────
const BrandManagement = lazy(() => import("../../features/brand/pages/BrandManagement"));
const AddBrandImproved = lazy(() => import("../../features/brand/pages/AddBrandImproved"));
const AddCategory = lazy(() => import("../../features/category").then(named("AddCategory")));
const CategoryList = lazy(() => import("../../features/category").then(named("CategoryList")));
const AddProduct = lazy(() => import("../../features/product").then(named("AddProduct")));
const EditProduct = lazy(() => import("../../features/product/pages/EditProduct"));
const ProductManagement = lazy(() => import("../../features/product/pages/ProductManagement"));

// ── Orders + printable views ────────────────────────────────
const OrderList = lazy(() => import("../../features/orders").then(named("OrderList")));
const AddOrder = lazy(() => import("../../features/orders").then(named("AddOrder")));
const OrderDetails = lazy(() => import("../../features/orders").then(named("OrderDetails")));
const OrderPrintLabel = lazy(() => import("../../features/orders").then(named("OrderPrintLabel")));
const OrderPrintPackingSlip = lazy(() => import("../../features/orders").then(named("OrderPrintPackingSlip")));

// ── Customers + Support + Promo + Newsletter + Notifications ─
const CustomerList = lazy(() => import("../../features/customers").then(named("CustomerList")));
const AddCustomer = lazy(() => import("../../features/customers").then(named("AddCustomer")));
const CustomerDetails = lazy(() => import("../../features/customers").then(named("CustomerDetails")));
const SupportTicketList = lazy(() => import("../../features/support-tickets").then(named("SupportTicketList")));
const AddSupportTicket = lazy(() => import("../../features/support-tickets").then(named("AddSupportTicket")));
const PromoCodeList = lazy(() => import("../../features/promo-codes").then(named("PromoCodeList")));
const AddPromoCode = lazy(() => import("../../features/promo-codes").then(named("AddPromoCode")));
const NewsletterList = lazy(() => import("../../features/newsletter-management").then(named("NewsletterList")));
const AddNewsletter = lazy(() => import("../../features/newsletter-management").then(named("AddNewsletter")));
const SendNotificationList = lazy(() => import("../../features/send-notification/pages").then(named("SendNotificationList")));
const AddSendNotification = lazy(() => import("../../features/send-notification/pages").then(named("AddSendNotification")));

// ── Image / Featured Section / Return Request / Flash Sale / Blog ─
const HomeSliderImages = lazy(() => import("../../features/image-management/components/HomeSliderImageList"));
const NewOffersImages = lazy(() => import("../../features/image-management/components/NewOffersImageList"));
const AddHomeSliderImage = lazy(() => import("../../features/image-management/pages").then(named("AddHomeSliderImage")));
const AddNewOffersImage = lazy(() => import("../../features/image-management/pages").then(named("AddNewOffersImage")));
const ReturnRequestList = lazy(() => import("../../features/return-requests/pages").then(named("ReturnRequestList")));
const AddReturnRequest = lazy(() => import("../../features/return-requests/pages").then(named("AddReturnRequest")));
const EditReturnRequest = lazy(() => import("../../features/return-requests/pages").then(named("EditReturnRequest")));
const FeaturedSectionList = lazy(() => import("../../features/featured-sections").then(named("FeaturedSectionList")));
const AddFeaturedSection = lazy(() => import("../../features/featured-sections").then(named("AddFeaturedSection")));
const EditFeaturedSection = lazy(() => import("../../features/featured-sections").then(named("EditFeaturedSection")));
const FlashSaleList = lazy(() => import("../../features/flashsales/pages").then(named("FlashSaleList")));
const AddFlashSale = lazy(() => import("../../features/flashsales/pages").then(named("AddFlashSale")));
const EditFlashSale = lazy(() => import("../../features/flashsales/pages").then(named("EditFlashSale")));
const BlogList = lazy(() => import("../../features/blog/pages").then(named("BlogList")));
const AddBlog = lazy(() => import("../../features/blog/pages").then(named("AddBlog")));
const EditBlog = lazy(() => import("../../features/blog/pages").then(named("EditBlog")));

// ── Delivery + Media ─────────────────────────────────────────
const DeliveryBoyList = lazy(() => import("../../features/delivery-boys/pages").then(named("DeliveryBoyList")));
const AddDeliveryBoy = lazy(() => import("../../features/delivery-boys/pages").then(named("AddDeliveryBoy")));
const EditDeliveryBoy = lazy(() => import("../../features/delivery-boys/pages").then(named("EditDeliveryBoy")));
const MediaManager = lazy(() => import("../../features/media/pages").then(named("MediaManager")));

// ── Warehouse ────────────────────────────────────────────────
// StockMovementPage replaces three separate pages (StockIn/Out/Adjust).
// The old paths still resolve to it so deep links keep working.
const WarehouseDashboard = lazy(() => import("../../features/warehouse/pages").then(named("WarehouseDashboard")));
const InventoryPage = lazy(() => import("../../features/warehouse/pages").then(named("InventoryPage")));
const StockMovementPage = lazy(() => import("../../features/warehouse/pages").then(named("StockMovementPage")));
const StockHistoryPage = lazy(() => import("../../features/warehouse/pages").then(named("StockHistoryPage")));
const LowStockPage = lazy(() => import("../../features/warehouse/pages").then(named("LowStockPage")));
const StockReportPage = lazy(() => import("../../features/warehouse/pages").then(named("StockReportPage")));
const PendingOrdersPage = lazy(() => import("../../features/warehouse/pages").then(named("PendingOrdersPage")));
const AssignmentsPage = lazy(() => import("../../features/warehouse/pages").then(named("AssignmentsPage")));

// ── Warehouse master + Serviceable Pincodes ──────────────────
// Separate namespace from /admin/warehouse/* above — these manage the
// warehouse entities themselves (add a new dark-store, map pincodes
// to it) instead of doing stock ops within one.
const WarehouseListPage = lazy(() => import("../../features/warehouse-master/pages").then(named("WarehouseList")));
const WarehouseFormPage = lazy(() => import("../../features/warehouse-master/pages").then(named("WarehouseForm")));
const WarehouseInventoryPage = lazy(() => import("../../features/warehouse-master/pages").then(named("WarehouseInventory")));
const LowStockDashboardPage = lazy(() => import("../../features/warehouse-master/pages").then(named("LowStockDashboard")));
const PincodeListPage = lazy(() => import("../../features/serviceable-pincodes/pages").then(named("PincodeList")));
const PincodeFormPage = lazy(() => import("../../features/serviceable-pincodes/pages").then(named("PincodeForm")));
const PincodeBulkAddPage = lazy(() => import("../../features/serviceable-pincodes/pages").then(named("PincodeBulkAdd")));
const BulkProductUpload = lazy(() => import("../../features/product/pages/BulkProductUpload"));

// ── Settings / banners / policies / trash / audit / services ─
const AppSettings = lazy(() => import("../../features/app-settings/pages/AppSettings"));
const AppReleases = lazy(() => import("../../features/app-releases/pages/AppReleases"));
const FcmSettings = lazy(() => import("../../features/fcm-settings").then(named("FcmSettings")));
const HomeBanners = lazy(() => import("../../features/home-banners/pages/HomeBanners"));
const LegalPolicies = lazy(() => import("../../features/legal-policies/pages/LegalPolicies"));
const TrashPage = lazy(() => import("../../features/trash/pages/TrashPage"));
const AuditLogs = lazy(() => import("../../features/audit-logs/pages/AuditLogs"));

// ── Integrations + analytics ─────────────────────────────────
const AnalyticsDashboard = lazy(() => import("../../features/analytics").then(named("AnalyticsDashboard")));
const PaymentList = lazy(() => import("../../features/payments").then(named("PaymentList")));
const PaymentDetail = lazy(() => import("../../features/payments").then(named("PaymentDetail")));
const Reports = lazy(() => import("../../features/reports").then(named("Reports")));
const VipList = lazy(() => import("../../features/vip").then(named("VipList")));
const VipDetail = lazy(() => import("../../features/vip").then(named("VipDetail")));
const WhatsAppTemplates = lazy(() => import("../../features/whatsapp").then(named("WhatsAppTemplates")));
const OrderFlowDiagram = lazy(() => import("../../features/docs/pages/OrderFlowDiagram"));
const OrderManagementGuide = lazy(() => import("../../features/docs/pages/OrderManagementGuide"));
const CashManagementPage = lazy(() => import("../../features/cash-management/pages/CashManagementPage"));

// List of routes with config
const adminRoutesConfig = [
  // Basic Pages
  { path: "/admin/home", component: Home },
  { path: "/admin/profile", component: Profile },
  { path: "/admin/settings", component: Settings },
  { path: "/admin", component: AdminDashboard },
  
  // User Management
  { path: "/admin/users/list", component: UserList },
  { path: "/admin/user/register", component: RegisterUserForm },
  { path: "/admin/roles", component: RolesList },
  
  // Brand Management
  { path: "/admin/brand/add", component: AddBrandImproved, onlyProtected: true },
  { path: "/admin/brand/edit/:id", component: AddBrandImproved, onlyProtected: true },
  { path: "/admin/brand/list", component: BrandManagement, onlyProtected: true },
  
  // Category Management
  { path: "/admin/category/add", component: AddCategory, onlyProtected: true },
  { path: "/admin/category/list", component: CategoryList, onlyProtected: true },
  
  // Product Management
  { path: "/admin/product/add", component: AddProduct, onlyProtected: true },
  { path: "/admin/product/edit/:id", component: EditProduct, onlyProtected: true },
  { path: "/admin/product/list", component: ProductManagement, onlyProtected: true },
  { path: "/admin/product/bulk-upload", component: BulkProductUpload, onlyProtected: true },
  
  // Order Management
  { path: "/admin/order/list", component: OrderList },
  { path: "/admin/order/add", component: AddOrder },
  { path: "/admin/order/details/:id", component: OrderDetails },
  
  // Customer Management
  { path: "/admin/customers/list", component: CustomerList },
  { path: "/admin/customer/add", component: AddCustomer },
  { path: "/admin/customer/details/:id", component: CustomerDetails },
  
  // Delivery Boys Management
  { path: "/admin/delivery-boys/list", component: DeliveryBoyList },
  { path: "/admin/delivery-boys/add", component: AddDeliveryBoy },

  // App Settings
  { path: "/admin/app-settings", component: AppSettings },
  { path: "/admin/app-releases", component: AppReleases },
  { path: "/admin/fcm-settings", component: FcmSettings },
  { path: "/admin/home-banners", component: HomeBanners },
  { path: "/admin/legal-policies", component: LegalPolicies },
  { path: "/admin/trash", component: TrashPage },
  { path: "/admin/audit-logs", component: AuditLogs },
  { path: "/admin/docs/order-flow", component: OrderFlowDiagram },
  { path: "/admin/docs/order-management", component: OrderManagementGuide },
  { path: "/admin/cash-management", component: CashManagementPage },
  
  // Support Tickets
  { path: "/admin/support-ticket/list", component: SupportTicketList },
  { path: "/admin/support-ticket/add", component: AddSupportTicket },
  
  // Promo Codes
  { path: "/admin/promo-codes/list", component: PromoCodeList },
  { path: "/admin/promo-codes/add", component: AddPromoCode },
  
  // Flash Sales
  { path: "/admin/flash-sales", component: FlashSaleList },
  { path: "/admin/flash-sales/add", component: AddFlashSale },
  
  // Blog Management
  { path: "/admin/blog", component: BlogList },
  { path: "/admin/blog/add", component: AddBlog },
  
  // Media Management
  { path: "/admin/media", component: MediaManager },
  
  // Image Management
  { path: "/admin/images/home-slider", component: HomeSliderImages },
  { path: "/admin/images/home-slider/add", component: AddHomeSliderImage },
  { path: "/admin/images/new-offers", component: NewOffersImages },
  { path: "/admin/images/new-offers/add", component: AddNewOffersImage },
  
  // Return Requests
  { path: "/admin/return-requests", component: ReturnRequestList },
  { path: "/admin/return-requests/add", component: AddReturnRequest },
  
  // Featured Sections
  { path: "/admin/featured-sections", component: FeaturedSectionList },
  { path: "/admin/featured-sections/add", component: AddFeaturedSection },
  
  // Newsletter Management
  { path: "/admin/newsletter-management", component: NewsletterList },
  { path: "/admin/newsletter-management/add", component: AddNewsletter },
  { path: "/admin/newsletter-management/edit/:id", component: AddNewsletter },
  
  // Send Notification
  { path: "/admin/send-notification", component: SendNotificationList },
  { path: "/admin/send-notification/add", component: AddSendNotification },

  // Analytics
  { path: "/admin/analytics", component: AnalyticsDashboard },

  // Payments
  { path: "/admin/payments", component: PaymentList },

  // Reports (CSV export)
  { path: "/admin/reports", component: Reports },

  // VIP wallet management
  { path: "/admin/vip", component: VipList },

  // WhatsApp templates (admin-configurable webhooks)
  { path: "/admin/whatsapp", component: WhatsAppTemplates },

  // Warehouse Management
  { path: "/admin/warehouse", component: WarehouseDashboard },
  { path: "/admin/warehouse/inventory", component: InventoryPage },
  // Stock In / Out / Adjust collapsed into a single tabbed page.
  // The three legacy URLs survive so deep links (Low Stock "Restock",
  // Inventory row actions) keep landing on the right tab.
  { path: "/admin/warehouse/stock-movement", component: StockMovementPage },
  { path: "/admin/warehouse/stock-in", component: StockMovementPage },
  { path: "/admin/warehouse/stock-out", component: StockMovementPage },
  { path: "/admin/warehouse/stock-adjust", component: StockMovementPage },
  { path: "/admin/warehouse/stock-history", component: StockHistoryPage },
  { path: "/admin/warehouse/low-stock", component: LowStockPage },
  { path: "/admin/warehouse/report", component: StockReportPage },
  { path: "/admin/warehouse/orders", component: PendingOrdersPage },
  { path: "/admin/warehouse/assignments", component: AssignmentsPage },

  // Warehouse master + serviceable-pincodes (Phase 1)
  { path: "/admin/warehouses", component: WarehouseListPage },
  { path: "/admin/warehouses/add", component: WarehouseFormPage },
  { path: "/admin/warehouses/:id/edit", component: WarehouseFormPage },
  { path: "/admin/warehouses/:id/inventory", component: WarehouseInventoryPage },
  { path: "/admin/warehouses/low-stock", component: LowStockDashboardPage },
  { path: "/admin/serviceable-pincodes", component: PincodeListPage },
  { path: "/admin/serviceable-pincodes/add", component: PincodeFormPage },
  { path: "/admin/serviceable-pincodes/:id/edit", component: PincodeFormPage },
  { path: "/admin/serviceable-pincodes/bulk", component: PincodeBulkAddPage },
];

// Map to routes array
const configuredRoutes = adminRoutesConfig.map(route => ({
  path: route.path,
  element: route.onlyProtected
    ? protectedElement(<route.component />)
    : adminElement(<route.component />)
}));

// Custom routes with parameters (edit routes, etc.)
const customRoutes = [
  // Edit routes with parameters
  {
    path: "/admin/delivery-boys/edit/:id",
    element: adminElement(<EditDeliveryBoy />)
  },
  {
    path: "/admin/promo-codes/edit/:id",
    element: adminElement(<AddPromoCode />)
  },
  {
    path: "/admin/flash-sales/edit/:id",
    element: adminElement(<EditFlashSale />)
  },
  {
    path: "/admin/blog/edit/:id",
    element: adminElement(<EditBlog />)
  },
  {
    path: "/admin/images/home-slider/edit/:id",
    element: adminElement(<AddHomeSliderImage />)
  },
  {
    path: "/admin/images/new-offers/edit/:id",
    element: adminElement(<AddNewOffersImage />)
  },
  {
    path: "/admin/return-requests/edit/:id",
    element: adminElement(<EditReturnRequest />)
  },
  {
    path: "/admin/featured-sections/edit/:id",
    element: adminElement(<EditFeaturedSection />)
  },
  {
    path: "/admin/vip/:id",
    element: adminElement(<VipDetail />)
  },
  {
    path: "/admin/payments/:id",
    element: adminElement(<PaymentDetail />)
  },
  // Printable views — auth-gated but no AdminLayout chrome, so
  // window.print() captures only the label/slip. Opened in new tabs.
  {
    path: "/admin/order/:id/print/label",
    element: printableElement(<OrderPrintLabel />)
  },
  {
    path: "/admin/order/:id/print/packing-slip",
    element: printableElement(<OrderPrintPackingSlip />)
  },
];

// Combine all routes
export const Admin = [...configuredRoutes, ...customRoutes];
